// DecisionCard — response card for a single LLM decision turn.
// Moved verbatim from DecisionEnginePage.js during Phase 2 of the System
// Consolidation. Owns its own Save-to-Player popover state and copy-to-
// clipboard state; no props or behavior changed.
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bookmark,
  BookmarkPlus,
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Search,
} from 'lucide-react';

import { API } from './helpers';
import { GpsChip } from './GpsChip';
import { RichText } from './RichText';
import { PredictionBadge } from './PredictionBadge';
import { DecisionValidationBadge } from './DecisionValidationBadge';
import { DecisionExplanationDisclosure } from './DecisionExplanationDisclosure';
import { ActionHintStrip } from './ActionHintStrip';

export const DecisionCard = ({ response, query = '', sessionId = null }) => {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [athletesLoaded, setAthletesLoaded] = useState(false);
  const [filter, setFilter] = useState('');

  const loadAthletes = async () => {
    if (athletesLoaded) return;
    try {
      const r = await axios.get(`${API}/athletes`);
      setAthletes(Array.isArray(r.data) ? r.data : []);
      setAthletesLoaded(true);
    } catch (e) {
      toast.error('Could not load athletes list');
    }
  };

  const handleSave = async (athlete) => {
    setSaving(true);
    setPickerOpen(false);
    try {
      await axios.post(`${API}/intelligence/save-decision`, {
        player_id: athlete.id,
        decision: {
          decision_summary: response.decision_summary || '',
          reasoning: response.reasoning || '',
          risk_notes: response.risk_notes || '',
          alternative_option: response.alternative_option || '',
        },
        source: query.startsWith('/') ? 'command' : 'manual',
        query,
        session_id: sessionId,
      });
      setSaved(true);
      toast.success(`Saved to ${athlete.full_name}'s record`);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const parts = [];
    if (response.decision_summary) parts.push(`Decision Summary:\n${response.decision_summary}`);
    if (response.reasoning) parts.push(`Reasoning:\n${response.reasoning}`);
    if (response.risk_notes) parts.push(`Risk Notes:\n${response.risk_notes}`);
    if (response.alternative_option) parts.push(`Alternative Option:\n${response.alternative_option}`);
    const text = parts.join('\n\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const filtered = filter
    ? athletes.filter((a) => (a.full_name || '').toLowerCase().includes(filter.toLowerCase()))
    : athletes;

  return (
    <Card className="border-l-4 border-l-indigo-500 relative" data-testid="decision-card">
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        <PredictionBadge prediction={response.prediction} />
        <Popover
          open={pickerOpen}
          onOpenChange={(open) => {
            setPickerOpen(open);
            if (open) loadAthletes();
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={saving || !response.decision_summary}
              data-testid="save-decision-btn"
              title="Save to a player's record"
            >
              {saved ? (
                <>
                  <Bookmark className="h-3.5 w-3.5 mr-1 fill-emerald-600 text-emerald-600" />
                  <span className="text-emerald-600">Saved</span>
                </>
              ) : saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <BookmarkPlus className="h-3.5 w-3.5 mr-1" />
                  Save
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="end" data-testid="save-player-popover">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input
                  autoFocus
                  placeholder="Search player…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-7 h-8 text-sm"
                  data-testid="save-player-search"
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto py-1" data-testid="save-player-list">
              {!athletesLoaded && (
                <div className="px-3 py-6 text-center text-xs text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                  Loading players…
                </div>
              )}
              {athletesLoaded && filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-gray-400">No players match.</div>
              )}
              {filtered.slice(0, 50).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => handleSave(a)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm"
                  data-testid={`save-player-option-${a.id}`}
                >
                  <div className="font-medium">{a.full_name}</div>
                  <div className="text-xs text-gray-500">{a.position || '—'} · {a.squad || ''}</div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2 text-xs"
          data-testid="copy-response-btn"
          title="Copy full response"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" />
              <span className="text-emerald-600">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5 mr-1" />
              Copy
            </>
          )}
        </Button>
      </div>
      <CardContent className="pt-5 pb-4 pr-32 sm:pr-72 space-y-4">
        {/* Deterministic safety verdict — always visible. */}
        <DecisionValidationBadge validation={response.decision_validation} />
        {/* Collapsed-by-default "Why?" audit trail for the validator. */}
        <DecisionExplanationDisclosure
          explanation={response.decision_explanation}
          status={response.decision_validation?.status}
        />
        {/* Action hint — derived deterministically from the prediction. */}
        <ActionHintStrip hint={response.action_hint} riskLevel={response.prediction?.risk_level} />
        {/* GPS trust indicator — single-player only */}
        {response.gps_available !== null && response.gps_available !== undefined && (
          <GpsChip available={response.gps_available} />
        )}
        {/* Decision Summary — prominent */}
        {response.decision_summary && (
          <div data-testid="section-decision-summary">
            <div className="flex items-center gap-2 mb-2">
              <ChevronRight className="h-4 w-4 text-indigo-600" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-400">Decision</h3>
            </div>
            <RichText
              text={response.decision_summary}
              className="text-base font-semibold text-gray-900 dark:text-gray-100"
              testId="decision-summary-body"
            />
          </div>
        )}

        {/* Reasoning — neutral */}
        {response.reasoning && (
          <div data-testid="section-reasoning">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Reasoning</h3>
            <RichText
              text={response.reasoning}
              className="text-sm text-gray-700 dark:text-gray-300"
              testId="reasoning-body"
            />
          </div>
        )}

        {/* Risk Notes — warning */}
        {response.risk_notes && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3" data-testid="section-risk-notes">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">Risk Notes</h3>
            </div>
            <RichText
              text={response.risk_notes}
              className="text-sm text-amber-900 dark:text-amber-200"
              testId="risk-notes-body"
            />
          </div>
        )}

        {/* Alternative — lighter */}
        {response.alternative_option && (
          <div className="border-t border-dashed pt-3" data-testid="section-alternative">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Alternative</h3>
            <RichText
              text={response.alternative_option}
              className="text-sm text-gray-600 dark:text-gray-400"
              testId="alternative-body"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
